import { Alert } from '@material-ui/lab';
import { Size } from '@ps/web-core-enums';
import { generatePath } from '@ps/web-core-utils';
import {
  StarsButton as Button,
  Link,
  Translation,
  useIsMounted,
  useScreenSize
} from '@ps/web-shared-components';
import { StarsLink } from '@ps/web-shared-ui';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useRouteMatch } from 'react-router-dom';

import WebpWithFallback from '@swf/components/WebpWithFallback/WebpWithFallback';
import useSignup from '@swf/core/hooks/useSignup';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import bannerDesktopPNGPromo from '../assets/banner-desktop-promo.png';
import bannerDesktopPromo from '../assets/banner-desktop-promo.webp';
import bannerDesktopPNG from '../assets/banner-desktop.png';
import bannerDesktop from '../assets/banner-desktop.webp';
import bannerMobilePNGPromo from '../assets/banner-mobile-tablet-promo.png';
import bannerMobilePromo from '../assets/banner-mobile-tablet-promo.webp';
import bannerMobilePNG from '../assets/banner-mobile-tablet.png';
import bannerMobile from '../assets/banner-mobile-tablet.webp';
import logoPNG from '../assets/logo.png';
import logo from '../assets/logo.webp';

import {
  TXTCLI_Login,
  TXTCLI_Reload,
  TXTMOB_Something_went_wrongX_Please_t_ELL,
  TXTWCL_If_you_still_experience_proble_ELL,
  TXTWCL_Join,
  TXTWCL_Must_deposit_to_play_real_mone_ELL,
  TXTWCL_Play_poker_with_your_friends,
  TXTWCL_Please_play_responsibly
} from './Banner.messages.json';
import styles from './Banner.scss';

export const Banner: React.FC<{ errorCode?: string }> = ({ errorCode }) => {
  const loading = useSelector((rootState: RootState) => !!rootState.loading?.models.auth);
  const isEmbedded = useSelector(select.device.isEmbedded);
  const { homeGamesPromotion = '' } = useSelector(select.cms.getAliases);
  const {
    modal: { enqueue }
  } = useDispatch<Dispatch>();
  const openLoginModal = useCallback(() => enqueue({ path: '/auth/login' }), [enqueue]);
  const signup = useSignup();
  const { greaterThan } = useScreenSize();
  const location = useLocation();
  const promoBasePath = generatePath(homeGamesPromotion, {});
  const promoPath = generatePath(homeGamesPromotion, { clubId: ':clubId', clubName: ':clubName' });
  const routeMatch = useRouteMatch<{ clubName: string }>(promoPath);
  const isMounted = useIsMounted();

  const isMobileDevice = !greaterThan(Size.Medium);
  const isPromo = homeGamesPromotion ? location.pathname.includes(promoBasePath) : false;
  const promoClubName = routeMatch?.params.clubName;
  const bannerMobileSRC = isPromo ? bannerMobilePromo : bannerMobile;
  const bannerMobileFallback = isPromo ? bannerMobilePNGPromo : bannerMobilePNG;
  const bannerDesktopSRC = isPromo ? bannerDesktopPromo : bannerDesktop;
  const bannerDesktopFallback = isPromo ? bannerDesktopPNGPromo : bannerDesktopPNG;

  return (
    <section className={styles.banner}>
      {isMobileDevice ? (
        <WebpWithFallback
          src={bannerMobileSRC}
          srcFallback={bannerMobileFallback}
          containerClassName={styles.mobileBanner}
          imageSize={{ height: 1280, width: 621 }}
        />
      ) : (
        <WebpWithFallback
          src={bannerDesktopSRC}
          srcFallback={bannerDesktopFallback}
          containerClassName={styles.desktopBanner}
          imageSize={{ height: 996, width: 1835 }}
        />
      )}
      <Link href="#" className={styles.logo}>
        <WebpWithFallback
          src={logo}
          srcFallback={logoPNG}
          imageSize={{ height: 711, width: 617 }}
        />
      </Link>
      <div className={styles.bannerInner}>
        <div className={styles.right}>
          <h1 className={styles.heading}>
            {isPromo ? promoClubName : <Translation {...TXTWCL_Play_poker_with_your_friends} />}
          </h1>
          {!isEmbedded && (
            <div className={styles.auth}>
              <Button
                className={styles.button}
                size="extra-large"
                inverse
                loading={loading}
                onClick={signup}
                translation={TXTWCL_Join}
              />
              <Button
                className={styles.button}
                inverse
                loading={loading}
                onClick={openLoginModal}
                translation={TXTCLI_Login}
                size="extra-large"
                variant="casino-glass"
              />
            </div>
          )}
          <div className={styles.terms}>
            <Translation {...TXTWCL_Must_deposit_to_play_real_mone_ELL} /> <br />
            <StarsLink
              href="https://www.begambleaware.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              BeGambleAware.org
            </StarsLink>{' '}
            <Translation {...TXTWCL_Please_play_responsibly} />
            {isMounted() && '.'}
          </div>
          {!!errorCode && (
            <Alert severity="error" variant="filled" className={styles.error}>
              <Translation {...TXTMOB_Something_went_wrongX_Please_t_ELL} />
              <Translation {...TXTWCL_If_you_still_experience_proble_ELL} />
              <br />
              <span>{`(${errorCode})`}</span>
              <br />
              <Button
                onClick={() => window.location.reload()}
                translation={TXTCLI_Reload}
                size="small"
              />
            </Alert>
          )}
        </div>
      </div>
    </section>
  );
};
